import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useJob, useJobApplications } from '../hooks/useApi';
import { FunnelBoard } from '../components/FunnelBoard';
import { FunnelInsightsPanel } from '../components/FunnelInsightsPanel';
import { CandidateDrawer } from '../components/CandidateDrawer';
import { srStageToFunnel, ACTIVE_STAGES } from '../components/FunnelStageBadge';
import type { FunnelStage } from '../components/FunnelStageBadge';
import type { Application } from '../types';

const TERMINAL_STAGES: FunnelStage[] = ['rejected', 'withdrawn'];
const ALL_STAGES: FunnelStage[] = [...ACTIVE_STAGES, 'placed', ...TERMINAL_STAGES];

function emptyBuckets(): Record<FunnelStage, Application[]> {
  return Object.fromEntries(ALL_STAGES.map((s) => [s, []])) as Record<FunnelStage, Application[]>;
}

export default function FunnelView() {
  const { id } = useParams<{ id: string }>();
  const jobId = Number(id);
  const navigate = useNavigate();
  const [selectedAppId, setSelectedAppId] = useState<number | null>(null);

  const { data: job, isLoading: jobLoading } = useJob(jobId);
  const { data: appData, isLoading: appsLoading, isError } = useJobApplications(jobId, 1, 500, 'createdAt', 'desc');

  const buckets = useMemo(() => {
    const result = emptyBuckets();
    for (const app of appData?.data ?? []) {
      const stage = srStageToFunnel[app.srStage?.toUpperCase() ?? ''] ?? 'lead';
      result[stage].push(app);
    }
    return result;
  }, [appData]);

  const isLoading = jobLoading || appsLoading;

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-sm text-stone-400 dark:text-gray-500">
        <Link to="/jobs" className="hover:text-stone-600 dark:hover:text-gray-300">Jobs</Link>
        <span>›</span>
        <Link to={`/jobs/${jobId}`} className="hover:text-stone-600 dark:hover:text-gray-300">
          {job?.title ?? '…'}
        </Link>
        <span>›</span>
        <span className="text-stone-700 dark:text-gray-200 font-medium">Funnel</span>
      </div>

      {/* Page header */}
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex-1">
          {job?.title ?? '…'}
        </h1>

        {/* View toggle */}
        <div className="flex gap-0.5 bg-stone-200 dark:bg-gray-700 rounded-lg p-0.5">
          <button
            onClick={() => navigate(`/jobs/${jobId}`)}
            className="text-xs font-medium px-3 py-1.5 rounded-md text-stone-500 dark:text-gray-400 hover:text-stone-700 dark:hover:text-gray-200"
          >
            ☰ Table
          </button>
          <button
            aria-label="funnel view"
            className="text-xs font-medium px-3 py-1.5 rounded-md bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-stone-200 dark:ring-gray-600"
          >
            ⊞ Funnel
          </button>
        </div>
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="flex gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="min-w-[220px] flex-shrink-0 bg-stone-50 dark:bg-gray-900 border border-stone-200 dark:border-gray-700 rounded-[10px] p-3 space-y-2">
              <div className="h-4 w-24 animate-pulse bg-stone-200 dark:bg-gray-700 rounded" />
              {Array.from({ length: 3 }).map((_, j) => (
                <div key={j} className="h-16 animate-pulse bg-stone-200 dark:bg-gray-700 rounded-lg" />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="card p-6 text-center dark:bg-gray-800">
          <p className="text-red-600 dark:text-red-400 mb-3">Failed to load applications.</p>
          <button onClick={() => window.location.reload()} className="btn btn-primary">Retry</button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && (appData?.data ?? []).length === 0 && (
        <div className="card p-10 text-center text-stone-400 dark:text-gray-500 dark:bg-gray-800">
          No applications yet for this job.
        </div>
      )}

      {/* AI Insights panel */}
      {!isLoading && !isError && (appData?.data ?? []).length > 0 && (
        <FunnelInsightsPanel jobId={jobId} />
      )}

      {/* Board */}
      {!isLoading && !isError && (appData?.data ?? []).length > 0 && (
        <FunnelBoard
          buckets={buckets}
          jobId={jobId}
          onOpen={setSelectedAppId}
        />
      )}

      <CandidateDrawer applicationId={selectedAppId} onClose={() => setSelectedAppId(null)} />
    </div>
  );
}
